import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import AddIcon from "@mui/icons-material/Add";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import StarIcon from "@mui/icons-material/Star";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

import AppCard from "@plannel/components/ui/AppCard";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import PaginationContainer from "@plannel/components/PaginationContainer";
import { LIST_GROUP_HEADER_SX } from "@plannel/components/ui/AutocompleteGroupHeader";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import { StyledToggleButton } from "@plannel/components/ui/ToggleButton";
import Snackbar from "@plannel/components/Snackbar";
import meetingAgendaService from "@plannel/services/system/meeting-agenda-service";
import { AGENDA_OPEN_EVENT, consumePendingAgendaId } from "./agendaDeepLink";
import CodeUtil from "@plannel/pages/utils/code-util";
import { FS } from "@plannel/utils/font-util";
import reduxUtil from "@plannel/utils/redux-util";
import ZDate from "@plannel/utils/zdate";

import { AGENDA_SCOPE, AGENDA_SCOPE_EVENT } from "./agendaScope";
import AgendaCard from "./components/AgendaCard";
import AgendaPeriodFilter from "./components/AgendaPeriodFilter";
import AgendaRow from "./components/AgendaRow";
import { AGENDA_STATUSES, getStatusLabelKey } from "./components/AgendaStatusChip";
import AgendaTagChip from "./components/AgendaTagChip";

/**
 * 모달은 Tiptap 을 끌고 들어온다(약 140 kB). 정적으로 import 하면 그 무게가 메인 번들에 실려
 * 안건 화면을 쓰지 않는 사용자까지 내려받는다. 모달이 열릴 때만 청크를 가져온다.
 */
const AgendaModal = lazy(() => import("./AgendaModal"));

/**
 * 카드 그리드의 한 페이지 개수. 사용자가 고르는 값이 아니라 레이아웃이라 고정한다 —
 * 다른 화면의 100/200/500 은 표라서 의미가 있지만 카드에는 맞지 않는다.
 * 정보 밀도는 페이지 개수가 아니라 뷰 토글(카드 그리드 / 한 줄 카드 / 표)로 조절한다.
 */
const PAGE_SIZE = 12;

/**
 * 날짜 범위를 서버가 받는 LocalDateTime 문자열로 만든다.
 *
 * 끝 날짜는 그 날 23:59:59 까지 늘린다. 9/20 을 끝으로 고르고 자정으로 보내면 9/20 15:00
 * 회의가 빠진다 — 사용자는 "9/20 까지"라고 골랐는데 9/20 이 통째로 사라지는 셈이다.
 */
const toServerDateTime = (date, endOfDay) => {
  if (!date) {
    return null;
  }
  const d = new ZDate(date);
  const pad = (n) => String(n).padStart(2, "0");
  const time = endOfDay ? "23:59:59" : "00:00:00";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${time}`;
};

/** 탭은 서버 필터의 프리셋일 뿐이다. "담당자" 탭은 P2 에서 붙는다. */
/**
 * 정보 밀도는 여기서 조절한다 — 한 페이지 개수가 아니라 렌더러를 바꾼다.
 * 표 뷰(시안 ③)는 ag-grid 설정이 따로 붙는 별개 작업이라 아직 없다.
 */
const VIEW_GRID = "GRID";
const VIEW_ROW = "ROW";

/** 아이콘을 앞에 다는 탭은 MUI 기본 높이(72)로 커진다 — 원래 높이로 되돌린다 */
const TAB_SX = { fontSize: FS.BODY, minHeight: 40, py: 0, gap: 0.75 };

// 값은 agendaScope.js 가 단일 소스다 — 홈의 회의 안건 카드가 같은 값으로 이 화면의 탭을 연다.
const SCOPE_ALL = AGENDA_SCOPE.ALL;
const SCOPE_STARRED = AGENDA_SCOPE.STARRED;
const SCOPE_MINE = AGENDA_SCOPE.MINE;
const SCOPE_DRAFT = AGENDA_SCOPE.DRAFT;

const MeetingAgenda = ({ t, viewName }) => {
  const reduxDispatch = useDispatch();
  const reduxViewState = reduxUtil.getViewState(viewName);

  // TextFieldSearch 는 state 가 아니라 ref 를 받는다 (엔터로만 조회한다)
  const keyword = useRef(reduxViewState?.keyword ?? "");

  const [scope, setScope] = useState(reduxViewState?.scope ?? SCOPE_ALL);
  const [statusCd, setStatusCd] = useState(reduxViewState?.statusCd ?? "");
  const [tagNm, setTagNm] = useState(reduxViewState?.tagNm ?? "");
  const [urgentOnly, setUrgentOnly] = useState(reduxViewState?.urgentOnly ?? false);
  // 끝난 회의는 기본으로 안 보인다 — 지워지지 않고 쌓여서 앞으로 있을 회의를 덮는다
  const [includeClosed, setIncludeClosed] = useState(reduxViewState?.includeClosed ?? false);

  // 날짜 축(회의 시간 / 작성 시간)과 그 범위. 기본은 범위 없음 — 처음 열면 전체가 보여야 한다.
  const [selectedSearch, setSelectedSearch] = useState(reduxViewState?.selectedSearch ?? "meeting");
  const [period, setPeriod] = useState({
    start: reduxViewState?.period?.start ? new ZDate(reduxViewState.period.start) : null,
    end: reduxViewState?.period?.end ? new ZDate(reduxViewState.period.end) : null
  });
  const [view, setView] = useState(reduxViewState?.view ?? VIEW_GRID);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const pageSize = PAGE_SIZE;

  const [agendas, setAgendas] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  // 미리 깔아 둔 것과 사용자가 만든 것을 나눠 들고 있는다 — 드롭다운에서 구분해 보여 준다
  const [tagOptions, setTagOptions] = useState({ predefined: [], used: [] });
  const [loading, setLoading] = useState(true);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  // 모달 하나로 보기와 편집을 겸한다. selected 가 null 이면 새 안건.
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const hasFilter = Boolean(
    keyword.current || statusCd || tagNm || urgentOnly || scope !== SCOPE_ALL || period.start || period.end
  );

  const retrieve = useCallback(
    (page = currentPage) => {
      setLoading(true);
      const from = toServerDateTime(period.start, false);
      const to = toServerDateTime(period.end, true);
      const byMeeting = selectedSearch === "meeting";

      meetingAgendaService
        .list({
          keyword: keyword.current || null,
          scope: "TITLE_BODY",
          statusCds: scope === SCOPE_DRAFT ? ["DRAFT"] : statusCd ? [statusCd] : null,
          tagNms: tagNm ? [tagNm] : null,
          urgentOnly,
          includeClosed,
          starredOnly: scope === SCOPE_STARRED,
          assignedToMe: scope === SCOPE_MINE,
          // 고른 축에만 범위를 싣는다. 두 축에 같은 범위를 걸면 회의 시간과 작성 시간을
          // 동시에 만족하는 것만 남아 결과가 거의 사라진다.
          meetingFrom: byMeeting ? from : null,
          meetingTo: byMeeting ? to : null,
          createdFrom: byMeeting ? null : from,
          createdTo: byMeeting ? null : to,
          page: page - 1, // 화면은 1부터, 서버는 0부터
          pageSize
        })
        .then((res) => {
          setAgendas(res.data?.results ?? []);
          setTotalPages(res.data?.totalPages ?? 0);
          setTotalItems(res.data?.totalItems ?? 0);
        })
        .catch(() => {
          setAgendas([]);
          setSnackInfo({ open: true, severity: "error", content: t("MSG_NetworkError") });
        })
        .then(() => {
          setLoading(false);
          const reduxState = reduxUtil.createObjectState(viewName, {
            keyword: keyword.current,
            scope,
            statusCd,
            tagNm,
            urgentOnly,
            includeClosed,
            selectedSearch,
            period,
            view,
            currentPage: page,
            pageSize
          });
          reduxDispatch(reduxUtil.updateViewState(reduxState));
        });
    },
    [
      currentPage,
      pageSize,
      scope,
      statusCd,
      tagNm,
      urgentOnly,
      includeClosed,
      selectedSearch,
      period,
      view,
      viewName,
      reduxDispatch,
      t
    ]
  );

  useEffect(() => {
    retrieve(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, scope, statusCd, tagNm, urgentOnly, includeClosed, selectedSearch, period]);

  useEffect(() => {
    meetingAgendaService
      .tags()
      .then((res) => {
        const result = res.data?.result ?? {};
        setTagOptions({ predefined: result.predefined ?? [], used: result.used ?? [] });
      })
      .catch(() => setTagOptions({ predefined: [], used: [] }));
  }, []);

  /*
   * 필터에 보이는 태그.
   *
   * 미리 깔아 둔 것은 <b>아직 아무 안건에도 안 붙었어도 보여 준다</b>. 처음엔 실제로 걸리는
   * 것만 넣었는데(빈 결과를 막으려고), 그러면 제품이 정해 둔 축이 목록에 아예 없어서
   * 무엇으로 거를 수 있는지 알 수가 없다. 빈 결과는 "그 태그를 쓴 안건이 없다" 는 답이라
   * 틀린 화면이 아니다.
   *
   * 우리가 만든 태그는 실제로 쓰인 것만 — 그쪽은 정해진 목록이 없으니 "쓰이는 것" 이 곧 전부다.
   */
  const tagKeyOf = (tag) => String(tag ?? "").trim().toLowerCase().replace(/\s+/g, "");
  const predefinedTags = tagOptions.predefined ?? [];
  const predefinedKeys = new Set(predefinedTags.map(tagKeyOf));
  const teamTagNames = (tagOptions.used ?? []).filter((tag) => !predefinedKeys.has(tagKeyOf(tag)));

  /*
    밖에서(홈의 회의 안건 카드) 이 화면을 열면서 탭을 지정한 경우.

    탭이 아직 없으면 위의 초기값(reduxViewState.scope)이 처리하므로 여기까지 오지 않는다 —
    이 리스너는 **이미 열려 있는 탭** 전용이다. 비활성 탭은 숨겨질 뿐 언마운트되지 않아서
    초기값을 다시 읽을 기회가 없고, 그대로 두면 화면만 넘어오고 탭은 보던 것 그대로 남는다.

    scope 와 currentPage 는 조회 effect 의 의존성이라 바꾸면 알아서 다시 조회한다.
    이미 그 탭이면 setState 가 같은 값이라 아무 일도 일어나지 않는다(불필요한 재조회 없음).
  */
  useEffect(() => {
    const onScope = (event) => {
      const next = event?.detail?.scope;
      // 모르는 값이면 무시한다 — 탭에 없는 범위를 넣으면 어느 탭도 선택되지 않은 화면이 된다
      if (!next || !Object.values(AGENDA_SCOPE).includes(next)) {
        return;
      }
      setScope(next);
      setCurrentPage(1);
    };
    window.addEventListener(AGENDA_SCOPE_EVENT, onScope);
    return () => window.removeEventListener(AGENDA_SCOPE_EVENT, onScope);
  }, []);

  /** 1페이지로 되돌리고 조회한다. 필터를 바꿨는데 3페이지에 머물면 빈 화면이 나온다 */
  const initPageToRetrieve = () => {
    if (currentPage === 1) {
      retrieve(1);
    } else {
      setCurrentPage(1);
    }
  };

  const handleToggleStar = (agenda) => {
    meetingAgendaService
      .toggleStar(agenda.id)
      .then((res) => {
        const starred = res.data?.result ?? !agenda.starred;
        setAgendas((prev) =>
          prev.map((row) => (row.id === agenda.id ? { ...row, starred } : row))
        );
      })
      .catch(() => setSnackInfo({ open: true, severity: "error", content: t("MSG_NetworkError") }));
  };

  /** 카드를 열 때 상세를 다시 받는다 — 목록 응답에는 본문 HTML 이 실려 있지 않다 */
  const handleOpen = (agenda) => {
    if (!agenda) {
      setSelected(null);
      setModalOpen(true);
      return;
    }
    setSaving(true);
    meetingAgendaService
      .get(agenda.id)
      .then((res) => {
        setSelected(res.data?.result ?? null);
        setModalOpen(true);
      })
      .catch(() => setSnackInfo({ open: true, severity: "error", content: t("MSG_NetworkError") }))
      .then(() => setSaving(false));
  };

  /**
   * 메일 링크(`/?agenda=123`)나 앱 안의 요청으로 안건 하나를 바로 연다.
   *
   * <p>목록을 거치지 않으므로 <b>없는 안건</b>을 지목할 수 있다 — 지워졌거나, 다른 테넌트
   * 계정으로 로그인했거나, Draft 라 남이 볼 수 없는 경우다. 그때 "네트워크 오류" 라고 하면
   * 사용자는 새로고침을 반복하게 된다. 열 수 없다는 사실을 그대로 말한다.
   */
  const openFromLink = (agendaId) => {
    if (!agendaId) {
      return;
    }
    setSaving(true);
    meetingAgendaService
      .get(agendaId)
      .then((res) => {
        const found = res.data?.result;
        if (!found) {
          throw new Error("agenda not found");
        }
        setSelected(found);
        setModalOpen(true);
      })
      .catch(() =>
        setSnackInfo({ open: true, severity: "warning", content: t("MSG_MeetingAgendaLinkNotFound") })
      )
      .then(() => setSaving(false));
  };

  /*
   * 링크로 지목된 안건을 집어간다.
   *
   * 두 갈래다. 화면이 지금 막 열렸으면 저장된 값을 집어가고(메일 링크로 들어온 경우),
   * 이미 탭으로 열려 있으면 mount 가 다시 일어나지 않으므로 이벤트로 받는다.
   */
  useEffect(() => {
    openFromLink(consumePendingAgendaId());
    const onOpenAgenda = (e) => {
      // 보관된 값도 같이 비운다. 이벤트로 이미 열었는데 남겨 두면 다음 mount 때 또 열린다
      consumePendingAgendaId();
      openFromLink(e?.detail?.agendaId);
    };
    window.addEventListener(AGENDA_OPEN_EVENT, onOpenAgenda);
    return () => window.removeEventListener(AGENDA_OPEN_EVENT, onOpenAgenda);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setSelected(null);
  };

  /**
   * 저장(필요하면 발행까지).
   *
   * <p>{@code afterSave} 는 <b>모달이 닫히기 전에</b> 실행된다. 새 안건에 끌어다 놓은 파일은
   * 서버가 요구하는 agendaId 가 없어 브라우저에 들고 있다가 여기서 올린다 — 닫은 뒤에 올리면
   * 실패해도 알릴 화면이 없다.
   */
  const persist = (payload, publish, afterSave) => {
    setSaving(true);
    /*
     * 메일 결과는 저장(또는 이어지는 발행) 응답에 실린다. 마지막 스낵바에서 쓰려고 여기서
     * 붙들어 둔다.
     */
    let mailed = 0;
    meetingAgendaService
      .upsert(payload)
      .then((res) => {
        mailed = res.data?.mailedCount ?? 0;
        const saved = res.data?.result;
        if (publish && saved?.id && saved.statusCd !== "PUBLISHED") {
          return meetingAgendaService.publish(saved.id).then((r) => {
            // 발행 경로에서도 메일이 나갈 수 있다 — 초안에 골라 둔 계획이 여기서 실행된다
            mailed = r.data?.mailedCount ?? mailed;
            return r.data?.result;
          });
        }
        return saved;
      })
      .then((saved) => (afterSave ? Promise.resolve(afterSave(saved)).then(() => saved) : saved))
      .then(() => {
        /*
         * 메일은 저장과 <b>다른 일</b>이라 결과도 따로 말한다.
         *
         * -1 은 한 통도 못 보냈다는 뜻이다(0 은 보낼 사람이 없었던 것). 안건은 이미 저장돼
         * 있으므로 "저장 실패" 로 말하면 안 된다 — 그러면 다시 쓰게 되고 안건이 두 벌이 된다.
         */
        setSnackInfo(
          mailed === -1
            ? { open: true, severity: "warning", content: t("MSG_MeetingAgendaMailFailed") }
            : mailed > 0
              ? { open: true, severity: "success", content: t("MSG_MeetingAgendaMailSent", { count: mailed }) }
              : { open: true, severity: "success", content: t("MSG_SaveSuccess") }
        );
        closeModal();
        retrieve(currentPage);
      })
      .catch(() => setSnackInfo({ open: true, severity: "error", content: t("MSG_SaveFailed") }))
      .then(() => setSaving(false));
  };

  const handleDelete = (agenda) => {
    if (!agenda?.id) {
      closeModal();
      return;
    }
    setSaving(true);
    meetingAgendaService
      .remove(agenda.id)
      .then(() => {
        setSnackInfo({ open: true, severity: "success", content: t("MSG_DeleteSuccess") });
        closeModal();
        retrieve(1);
      })
      .catch(() => setSnackInfo({ open: true, severity: "error", content: t("MSG_DeleteFailed") }))
      .then(() => setSaving(false));
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={view === VIEW_ROW
          ? { display: "flex", flexDirection: "column", gap: 1 }
          : { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 2 }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <AppCard key={index} sx={{ p: 2.25 }}>
              <Skeleton variant="rounded" width={80} height={20} />
              <Skeleton variant="text" sx={{ fontSize: FS.TITLE, mt: 1.5 }} />
              <Skeleton variant="text" width="90%" />
              <Skeleton variant="text" width="60%" />
            </AppCard>
          ))}
        </Box>
      );
    }

    if (agendas.length === 0) {
      const filtered = hasFilter;
      return (
        <AppCard sx={{ flexGrow: 1, display: "flex" }}>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              p: 4,
              textAlign: "center"
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "action.hover",
                color: "text.disabled"
              }}
            >
              {filtered ? <SearchOffIcon fontSize="large" /> : <EventNoteOutlinedIcon fontSize="large" />}
            </Box>
            <Typography sx={{ fontSize: FS.TITLE, fontWeight: 600, color: "text.primary" }}>
              {t(filtered ? "MSG_MeetingAgendaNoMatch" : "MSG_MeetingAgendaEmpty")}
            </Typography>
            <Typography sx={{ fontSize: FS.BODY, color: "text.secondary", maxWidth: 360 }}>
              {t(filtered ? "MSG_MeetingAgendaNoMatchHint" : "MSG_MeetingAgendaEmptyHint")}
            </Typography>
            {filtered ? (
              <Button
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={() => {
                  keyword.current = "";
                  setStatusCd("");
                  setTagNm("");
                  setScope(SCOPE_ALL);
                  setPeriod({ start: null, end: null });
                  setCurrentPage(1);
                }}
              >
                {t("MSG_MeetingAgendaResetFilters")}
              </Button>
            ) : (
              <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => handleOpen(null)}>
                {t("meetingAgendaNew")}
              </Button>
            )}
          </Box>
        </AppCard>
      );
    }

    if (view === VIEW_ROW) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {agendas.map((agenda) => (
            <AgendaRow
              key={agenda.id}
              agenda={agenda}
              t={t}
              onOpen={handleOpen}
              onToggleStar={handleToggleStar}
            />
          ))}
        </Box>
      );
    }

    return (
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 2 }}>
        {agendas.map((agenda) => (
          <AgendaCard
            key={agenda.id}
            agenda={agenda}
            t={t}
            onOpen={handleOpen}
            onToggleStar={handleToggleStar}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%", minHeight: 0 }}>
      {/* 화면 제목과 설명은 두지 않는다. 탭 바와 브레드크럼에 이미 화면 이름이 있고,
          다른 업무 화면도 본문에서 제목을 반복하지 않는다.

          FilterContainer 는 마지막 자식을 오른쪽 끝으로 민다. 그래서 필터들을 앞에 두고
          액션 버튼을 마지막에 둔다 — BomMaster 등이 쓰는 배치다.
          columnGap 은 필터끼리 맞붙는 것을 막는다. */}
      <FilterContainer sx={{ columnGap: 1.5 }}>
        <TextFieldSearch
          label="meetingAgendaSearch"
          searchCode={keyword}
          retrieve={initPageToRetrieve}
          sx={{ width: 280 }}
        />
        <AgendaPeriodFilter
          conditionList={CodeUtil.meetingAgendaDateCondition}
          selectedSearch={selectedSearch}
          setSelectedSearch={setSelectedSearch}
          period={period}
          setPeriod={setPeriod}
          t={t}
        />
        <Select
          size="small"
          displayEmpty
          value={statusCd}
          onChange={(event) => setStatusCd(event.target.value)}
          sx={{ width: 160 }}
        >
          <MenuItem value="">{t("meetingAgendaStatusAll")}</MenuItem>
          {AGENDA_STATUSES.map((code) => (
            <MenuItem key={code} value={code}>
              {t(getStatusLabelKey(code))}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          displayEmpty
          value={tagNm}
          onChange={(event) => setTagNm(event.target.value)}
          sx={{ width: 160 }}
        >
          <MenuItem value="">{t("meetingAgendaTagAll")}</MenuItem>
          {/* 구분 줄은 색을 입힌다 — 편집기 드롭다운과 같은 규격을 쓴다 */}
          <ListSubheader sx={LIST_GROUP_HEADER_SX}>{t("meetingAgendaTagBasic")}</ListSubheader>
          {predefinedTags.map((tag) => (
            <MenuItem key={tag} value={tag}>
              <AgendaTagChip tag={tag} />
            </MenuItem>
          ))}
          {teamTagNames.length > 0 && (
            <ListSubheader sx={LIST_GROUP_HEADER_SX}>{t("meetingAgendaTagTeam")}</ListSubheader>
          )}
          {teamTagNames.map((tag) => (
            <MenuItem key={tag} value={tag}>
              <AgendaTagChip tag={tag} />
            </MenuItem>
          ))}
        </Select>
        {/*
          목록을 좁히는 켜고 끄기는 이 앱에서 체크박스다 — RP Review 의 "예외만" 과 같은
          자리, 같은 성격이다. 값이 둘뿐이라 드롭다운은 쓰지 않는다.
        */}
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              name="urgentOnly"
              checked={urgentOnly}
              onChange={(event) => setUrgentOnly(event.target.checked)}
            />
          }
          label={t("meetingAgendaPriorityUrgentOnly")}
          sx={{ display: "flex", mr: 0 }}
        />
        {/*
          상태를 직접 고르면 그쪽이 이긴다 — "Closed" 를 골라 놓고 이 체크가 꺼져 있다고
          아무것도 안 보이면 화면이 고장 난 것으로 읽힌다. 그때는 이 칸을 아예 내린다.
        */}
        {statusCd !== "CLOSED" && scope !== SCOPE_DRAFT && (
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                name="includeClosed"
                checked={includeClosed}
                onChange={(event) => setIncludeClosed(event.target.checked)}
              />
            }
            label={t("meetingAgendaShowClosed")}
            sx={{ display: "flex", mr: 0 }}
          />
        )}
        {/*
          새 안건이 앞, 보기 전환이 뒤다.
          앞의 것들(검색·상태·태그·기간·보기)은 전부 "지금 보이는 목록을 손보는" 컨트롤이고
          새 안건만 유일하게 무언가를 만든다 — 성격이 다른 것을 줄 끝에 두면 나머지와 섞인다.
        */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpen(null)}>
            {t("meetingAgendaNew")}
          </Button>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={(_event, next) => { if (next) setView(next); }}
            aria-label={t("meetingAgendaView")}
          >
            <StyledToggleButton value={VIEW_GRID} aria-label={t("meetingAgendaViewGrid")}>
              <Tooltip title={t("meetingAgendaViewGrid")}><GridViewIcon fontSize="small" /></Tooltip>
            </StyledToggleButton>
            <StyledToggleButton value={VIEW_ROW} aria-label={t("meetingAgendaViewRow")}>
              <Tooltip title={t("meetingAgendaViewRow")}><ViewAgendaOutlinedIcon fontSize="small" /></Tooltip>
            </StyledToggleButton>
          </ToggleButtonGroup>
        </Box>
      </FilterContainer>

      <Tabs
        value={scope}
        onChange={(_event, value) => {
          setScope(value);
          setCurrentPage(1);
        }}
        sx={{ minHeight: 40, borderBottom: 1, borderColor: "divider", "& .MuiTab-root": { minHeight: 40 } }}
      >
        {/*
          아이콘을 붙이는 이유: 글자 셋만 있으면 이 줄이 "필터" 로 읽히지 않는다.
          특히 즐겨찾기는 카드에 달린 별을 눌러 모은 것인데, 그 별과 여기가 눈으로
          이어지지 않으면 모아 놓고도 어디서 보는지 찾지 못한다.
        */}
        <Tab
          value={SCOPE_ALL}
          icon={<InboxOutlinedIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
          label={t("meetingAgendaScopeAll")}
          sx={TAB_SX}
        />
        <Tab
          value={SCOPE_STARRED}
          // 카드의 별과 같은 아이콘·같은 색. 색까지 같아야 "그 별" 이라는 게 전달된다
          icon={<StarIcon sx={{ fontSize: 16, color: "warning.main" }} />}
          iconPosition="start"
          label={t("meetingAgendaScopeStarred")}
          sx={TAB_SX}
        />
        <Tab
          value={SCOPE_MINE}
          icon={<AssignmentIndOutlinedIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
          label={t("meetingAgendaScopeMine")}
          sx={TAB_SX}
        />
        <Tab
          value={SCOPE_DRAFT}
          icon={<EditNoteOutlinedIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
          label={t("meetingAgendaScopeDrafts")}
          sx={TAB_SX}
        />
      </Tabs>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {renderBody()}
      </Box>

      {totalItems > 0 && (
        <PaginationContainer
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          setCurrentPage={setCurrentPage}
        />
      )}

      {/* 열렸을 때만 렌더해야 lazy 가 실제로 효과가 있다 — 항상 렌더하면 즉시 청크를 받는다 */}
      {modalOpen && (
        <Suspense fallback={null}>
          <AgendaModal
            open={modalOpen}
            agenda={selected}
            loading={saving}
            tagOptions={tagOptions}
            onClose={closeModal}
            onSave={(payload, afterSave) => persist(payload, false, afterSave)}
            onPublish={(payload, afterSave) => persist(payload, true, afterSave)}
            onDelete={handleDelete}
            // 모달에는 스낵바가 없다. 직책을 펼치다 실패했을 때 조용히 넘어가면
            // 고른 것이 사라진 이유를 알 수 없으므로 목록 화면의 스낵바를 빌려 쓴다
            onError={(message) => setSnackInfo({ open: true, severity: "error", content: message })}
            onToggleStar={(agenda) => {
              handleToggleStar(agenda);
              setSelected((prev) => (prev ? { ...prev, starred: !prev.starred } : prev));
            }}
            t={t}
          />
        </Suspense>
      )}

      <Snackbar
        open={snackInfo.open}
        severity={snackInfo.severity}
        content={snackInfo.content}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
      />
    </Box>
  );
};

export default withTranslation()(MeetingAgenda);
